/**
 * Boot do banco (ADR 0178): migrações, reparos de schema legado e backfills que todo
 * aparelho roda ao abrir o app. Vivia dentro de `db/client.ts`, preso ao op-sqlite; agora
 * fala só com a porta (`DbRuntime`), então o MESMO boot roda no celular, no SQLite do Node
 * (teste `db/__tests__/bootstrap.test.ts`) e, no Kontto Web completo, no navegador.
 *
 * Regra: nada aqui pode importar driver, React Native ou Expo. O pacote de migrações vem
 * de fora (no app, `drizzle/migrations.js`; no teste, os `.sql` lidos do disco).
 */

import { LEGACY_SCHEMA_REPAIRS, type LegacyRepairTable } from '@/db/legacySchemaRepairs';
import { calculateInvoiceDates } from '@/lib/invoiceMonth';
import { cleanupZombieInvoiceArtifacts } from '@/db/zombieInvoiceCleanup';
import { dedupeSeedCategories } from '@/db/dedupeSeedCategories';
import type { DbRuntime } from '@/db/sqlPort';

/** Formato do `drizzle/migrations.js` (journal + SQL por índice `m0000`, `m0001`, …). */
export interface MigrationBundle {
    journal: { entries: { idx: number; when: number; tag: string }[] };
    migrations: Record<string, string>;
}

/**
 * Roda as migrações Drizzle com transação REAL por migração.
 *
 * NÃO voltar pro `migrate()` do drizzle: ele executa tudo via `session.transaction` do
 * driver op-sqlite, que tem exatamente o bug documentado em `runInTransaction` — dispara
 * begin/callback/commit sem `await`, então o COMMIT sai logo após a 1ª statement e o resto
 * roda em autocommit, sem rollback possível. Se o processo morresse no meio de uma migração
 * multi-statement, o schema ficava meio-migrado SEM registro no journal: no boot seguinte a
 * migração re-rodava, falhava ("duplicate column name"/"table already exists") e TODAS as
 * migrações seguintes ficavam bloqueadas para sempre — num app local-first, a "recuperação"
 * era reinstalar e perder os dados. Aqui cada migração roda num BEGIN/COMMIT aguardado, com
 * o INSERT no journal NA MESMA transação: morreu no meio → rollback → re-roda inteira no
 * próximo boot.
 *
 * A contabilidade espelha o migrate() original (tabela __drizzle_migrations, hash vazio,
 * created_at = `when` do journal, comparação pelo created_at da última linha), então bases
 * já migradas pelo migrador antigo continuam exatamente de onde pararam.
 */
async function runMigrations(rt: DbRuntime, migrations: MigrationBundle): Promise<void> {
    await rt.execute(
        'CREATE TABLE IF NOT EXISTS "__drizzle_migrations" (id SERIAL PRIMARY KEY, hash text NOT NULL, created_at numeric)',
    );
    const last = await rt.execute(
        'SELECT created_at FROM "__drizzle_migrations" ORDER BY created_at DESC LIMIT 1',
    );
    const lastMillis = Number(last.rows[0]?.created_at ?? 0);

    for (const entry of migrations.journal.entries) {
        if (entry.when <= lastMillis) continue;
        const sqlText = (migrations.migrations as Record<string, string>)[
            `m${String(entry.idx).padStart(4, '0')}`
        ];
        if (!sqlText) throw new Error(`Missing migration: ${entry.tag}`);
        const statements = sqlText
            .split('--> statement-breakpoint')
            .map((s) => s.trim())
            .filter(Boolean);

        await rt.runInTransaction(async () => {
            for (const stmt of statements) {
                await rt.execute(stmt);
            }
            await rt.execute(
                'INSERT INTO "__drizzle_migrations" (hash, created_at) VALUES (?, ?)',
                ['', entry.when],
            );
        });
    }
}

async function getTableColumns(rt: DbRuntime, tableName: LegacyRepairTable): Promise<Set<string>> {
    const result = await rt.execute(`PRAGMA table_info(${tableName});`);
    return new Set(result.rows.map((row) => String(row.name)));
}

async function repairLegacySchema(rt: DbRuntime) {
    const entries = Object.entries(LEGACY_SCHEMA_REPAIRS) as [
        LegacyRepairTable,
        (typeof LEGACY_SCHEMA_REPAIRS)[LegacyRepairTable],
    ][];

    for (const [tableName, repairs] of entries) {
        const existingColumns = await getTableColumns(rt, tableName);

        for (const repair of repairs) {
            if (existingColumns.has(repair.name)) continue;
            console.log(`Repairing legacy schema: ${tableName}.${repair.name}`);
            await rt.execute(repair.sql);
            existingColumns.add(repair.name);
        }
    }
}

/**
 * Recalcula opening/closing/due de TODAS as faturas a partir do cartão + mês de referência.
 *
 * As datas da fatura são persistidas na criação e nunca eram recomputadas. A correção da
 * borda de fevereiro (janelas que se sobrepunham em 1 dia → ocorrência recorrente duplicada
 * em março) só valia para faturas NOVAS; faturas já gravadas com a janela antiga continuavam
 * duplicando. Este repair (idempotente: só atualiza linhas cuja data diverge da recalculada)
 * cura os dados existentes em todos os caminhos de leitura de uma vez. `calculateInvoiceDates`
 * é a MESMA função usada na criação, então não há risco de divergência de regra.
 * Ver docs/RECURRING_CREDIT_CARD_TROUBLESHOOTING.md (achado nº2, borda de fev).
 */
async function backfillInvoiceWindows(rt: DbRuntime) {
    const result = await rt.execute(
        `SELECT i.id AS id, i.reference_month AS reference_month,
                i.opening_date AS opening_date, i.closing_date AS closing_date, i.due_date AS due_date,
                c.closing_day AS closing_day, c.due_day AS due_day,
                c.closing_day_to_next_invoice AS closing_day_to_next_invoice
         FROM invoices i
         JOIN credit_cards c ON c.id = i.credit_card_id`,
    );

    let healed = 0;
    for (const row of result.rows) {
        const { openingDate, closingDate, dueDate } = calculateInvoiceDates(
            String(row.reference_month),
            Number(row.closing_day),
            Number(row.due_day),
            Number(row.closing_day_to_next_invoice) === 1,
        );
        if (
            openingDate === row.opening_date &&
            closingDate === row.closing_date &&
            dueDate === row.due_date
        ) {
            continue;
        }
        await rt.execute(
            `UPDATE invoices SET opening_date = ?, closing_date = ?, due_date = ? WHERE id = ?`,
            [openingDate, closingDate, dueDate, String(row.id)],
        );
        healed += 1;
    }

    if (healed > 0) {
        console.log(`Backfill: recomputed window for ${healed} invoice(s).`);
    }
}

/**
 * Backfill ADITIVO das tags de recorrência: a tabela recurring_transaction_tags nasce vazia,
 * então recorrências criadas antes da feature não têm tags na regra — só a ocorrência-âncora
 * (a 1ª) costuma ter tag, as demais não (a dor relatada). Este repair, pra cada regra que tem
 * ocorrência com tag mas a regra ainda sem tags, copia as tags da âncora pra REGRA (fonte de
 * verdade, propaga às futuras) e aplica essas tags nas ocorrências materializadas que estão
 * SEM tag nenhuma — NUNCA sobrescreve uma ocorrência já taguejada (preserva Exceções). É
 * idempotente: depois de rodar, a regra tem tags e a query de candidatos não a seleciona mais.
 */
async function backfillRecurringTags(rt: DbRuntime) {
    const candidates = await rt.execute(
        `SELECT DISTINCT t.recurring_id AS rid
         FROM transactions t
         JOIN transaction_tags tt ON tt.transaction_id = t.id
         WHERE t.recurring_id IS NOT NULL AND t.deleted_at IS NULL
           AND NOT EXISTS (SELECT 1 FROM recurring_transaction_tags rtt WHERE rtt.recurring_id = t.recurring_id)`,
    );
    if (candidates.rows.length === 0) return;

    let healed = 0;
    for (const candidate of candidates.rows) {
        const rid = String(candidate.rid);

        // Âncora = ocorrência mais antiga (por slot) que TEM tag — suas tags viram as da série.
        const anchor = await rt.execute(
            `SELECT t.id AS id FROM transactions t
             WHERE t.recurring_id = ? AND t.deleted_at IS NULL
               AND EXISTS (SELECT 1 FROM transaction_tags tt WHERE tt.transaction_id = t.id)
             ORDER BY COALESCE(t.recurring_occurrence_date, t.date) ASC, t.created_at ASC
             LIMIT 1`,
            [rid],
        );
        const anchorId = anchor.rows[0]?.id;
        if (!anchorId) continue;

        const tagRows = await rt.execute(
            `SELECT tag_id AS tag_id FROM transaction_tags WHERE transaction_id = ?`,
            [String(anchorId)],
        );
        const tagIds = tagRows.rows.map((r) => String(r.tag_id));
        if (tagIds.length === 0) continue;

        // Ocorrências materializadas SEM tag nenhuma (aditivo, nunca sobrescreve uma já taguejada).
        const untagged = await rt.execute(
            `SELECT id AS id FROM transactions
             WHERE recurring_id = ? AND deleted_at IS NULL
               AND NOT EXISTS (SELECT 1 FROM transaction_tags tt WHERE tt.transaction_id = transactions.id)`,
            [rid],
        );

        // Tudo desta regra numa transação: a regra é o "gate" de idempotência (a query de
        // candidatos exclui regras que já têm tag), então uma falha no meio NÃO pode deixar a
        // regra com tag mas ocorrências sem — isso a excluiria pra sempre dos candidatos,
        // deixando estado parcial permanente. Com BEGIN/COMMIT, uma falha faz ROLLBACK e a
        // regra volta a ser candidata no próximo boot. INSERT OR IGNORE blinda contra um
        // futuro UNIQUE e contra reprocesso. Falha numa regra não derruba o boot (segue p/ próxima).
        try {
            await rt.execute('BEGIN');
            for (const tagId of tagIds) {
                await rt.execute(
                    `INSERT OR IGNORE INTO recurring_transaction_tags (recurring_id, tag_id) VALUES (?, ?)`,
                    [rid, tagId],
                );
            }
            for (const occ of untagged.rows) {
                for (const tagId of tagIds) {
                    await rt.execute(
                        `INSERT OR IGNORE INTO transaction_tags (transaction_id, tag_id) VALUES (?, ?)`,
                        [String(occ.id), tagId],
                    );
                }
            }
            await rt.execute('COMMIT');
            healed += 1;
        } catch (error) {
            await rt.execute('ROLLBACK').catch(() => {});
            console.warn(`Backfill: skip recurring ${rid} (rolled back):`, error);
        }
    }

    if (healed > 0) {
        console.log(`Backfill: propagated tags for ${healed} recurring series.`);
    }
}

/**
 * Backfill ADITIVO da observação de recorrência: a coluna recurring_transactions.notes nasce
 * NULL, então séries criadas antes da feature só têm a nota na ocorrência-âncora (a 1ª) —
 * as demais nasciam sem (mesma dor das tags, relatada pelo Ramon). Pra cada regra sem nota
 * cuja série tem ocorrência com nota, copia a nota da âncora pra REGRA (fonte de verdade,
 * propaga às futuras) e pras ocorrências materializadas SEM nota — NUNCA sobrescreve uma
 * ocorrência que já tem (preserva Exceções). Idempotente: depois de rodar, a regra tem nota
 * e a query de candidatos não a seleciona mais. Não bumpa updated_at (migração de fundo,
 * não edição do usuário).
 */
async function backfillRecurringNotes(rt: DbRuntime) {
    const candidates = await rt.execute(
        `SELECT DISTINCT t.recurring_id AS rid
         FROM transactions t
         WHERE t.recurring_id IS NOT NULL AND t.deleted_at IS NULL
           AND t.notes IS NOT NULL AND t.notes != ''
           AND EXISTS (SELECT 1 FROM recurring_transactions r
                       WHERE r.id = t.recurring_id AND r.deleted_at IS NULL
                         AND (r.notes IS NULL OR r.notes = ''))`,
    );
    if (candidates.rows.length === 0) return;

    let healed = 0;
    for (const candidate of candidates.rows) {
        const rid = String(candidate.rid);

        // Âncora = ocorrência mais antiga (por slot) que TEM nota — sua nota vira a da série.
        const anchor = await rt.execute(
            `SELECT t.notes AS notes FROM transactions t
             WHERE t.recurring_id = ? AND t.deleted_at IS NULL
               AND t.notes IS NOT NULL AND t.notes != ''
             ORDER BY COALESCE(t.recurring_occurrence_date, t.date) ASC, t.created_at ASC
             LIMIT 1`,
            [rid],
        );
        const anchorNotes = anchor.rows[0]?.notes;
        if (!anchorNotes) continue;

        // Mesmo desenho do backfill das tags: a regra é o gate de idempotência, então regra e
        // ocorrências vão juntas numa transação — falha no meio faz ROLLBACK e a regra volta a
        // ser candidata no próximo boot (nunca fica "regra com nota, ocorrências sem" permanente).
        try {
            await rt.execute('BEGIN');
            await rt.execute(
                `UPDATE recurring_transactions SET notes = ? WHERE id = ?`,
                [String(anchorNotes), rid],
            );
            await rt.execute(
                `UPDATE transactions SET notes = ?
                 WHERE recurring_id = ? AND deleted_at IS NULL
                   AND (notes IS NULL OR notes = '')`,
                [String(anchorNotes), rid],
            );
            await rt.execute('COMMIT');
            healed += 1;
        } catch (error) {
            await rt.execute('ROLLBACK').catch(() => {});
            console.warn(`Backfill: skip recurring notes ${rid} (rolled back):`, error);
        }
    }

    if (healed > 0) {
        console.log(`Backfill: propagated notes for ${healed} recurring series.`);
    }
}

/**
 * Backfill ÚNICO do Aviso antecipado (ver docs/adr/0025): liga `advance_reminder_enabled`
 * nas despesas recorrentes que JÁ existem na categoria "Assinaturas" — "até pra quem já tem
 * cadastrado". Guardado por uma flag em `settings` para rodar SÓ UMA VEZ: sem isso, religaria
 * o flag todo boot e desfaria um desligamento manual do usuário. Disparar ainda exige Pro
 * (o scheduler checa), então em quem não é Pro o flag fica ligado mas inerte (gancho de
 * conversão). Idempotente: a flag de controle marca conclusão mesmo que 0 linhas mudem
 * (categoria renomeada/ausente).
 */
async function backfillAdvanceChargeReminders(rt: DbRuntime) {
    const DONE_KEY = 'advance_reminder_backfill_v1';
    const guard = await rt.execute(
        `SELECT value FROM settings WHERE key = ?`,
        [DONE_KEY],
    );
    if (guard.rows.length > 0) return;

    const now = new Date().toISOString();
    // NÃO bumpa updated_at: ligar o flag é migração de fundo, não edição do usuário — bumpar
    // faria todas as Assinaturas aparecerem como "alteradas agora" em qualquer ordenação por
    // updatedAt. Só seta a coluna.
    await rt.execute(
        `UPDATE recurring_transactions
            SET advance_reminder_enabled = 1
          WHERE deleted_at IS NULL
            AND type = 'expense'
            AND advance_reminder_enabled = 0
            AND category_id IN (
                SELECT id FROM categories WHERE name = 'Assinaturas' AND type = 'expense'
            )`,
    );
    await rt.execute(
        `INSERT OR REPLACE INTO settings (key, value, updated_at) VALUES (?, '1', ?)`,
        [DONE_KEY, now],
    );
}

/**
 * Backfill ÚNICO das compras de cartão que ficaram presas em "Pendente".
 *
 * Duas portas deixavam a linha de cartão fora do formato canônico (`is_confirmed = 1`, sem
 * conta — quem liquida é o pagamento da fatura):
 *
 *  1. **Converter um lançamento em parcelado** criava as parcelas 2..N com `isConfirmed`
 *     falso fixo, mesmo no cartão (o form já corrige; a série criada direto em modo cartão
 *     nunca teve o bug, `installmentRepository` grava 1). Por isso a condição do
 *     `installment_id`: numa série de cartão nenhuma parcela é previsto — o banco parcelou
 *     tudo na compra.
 *  2. **Recorrência de cartão e compra com data futura** nascem pendentes DE PROPÓSITO (é o
 *     previsto que a notificação abate, ADR 0056) — mas nada as efetivava depois, nem ao
 *     pagar a fatura. O `payInvoice` agora fecha isso na frente; aqui vale só para fatura
 *     JÁ quitada, com a mesma tolerância de meio centavo do `computeInvoiceStatus`.
 *
 * O estrago não era só o selo: "Concluídas" (= `is_confirmed = 1`) é o padrão do Relatório
 * personalizado e do Exportar, então gasto real de fatura paga sumia do PDF em silêncio.
 *
 * Fora do alcance: linha com conta (tem o efetivar dela) e artefato de fatura
 * (`adjustment_type`), que é do fluxo de pagamento. Guardado por flag em `settings` — quem
 * deixa uma compra futura pendente de propósito não a vê re-efetivada todo boot.
 */
async function backfillPendingCardEntries(rt: DbRuntime) {
    const DONE_KEY = 'card_entry_confirmation_backfill_v1';
    const guard = await rt.execute(
        `SELECT value FROM settings WHERE key = ?`,
        [DONE_KEY],
    );
    if (guard.rows.length > 0) return;

    const now = new Date().toISOString();
    // Não bumpa updated_at, mesma razão do backfill do Aviso antecipado: é reparo de fundo,
    // não edição do usuário.
    await rt.execute(
        `UPDATE transactions
            SET is_confirmed = 1
          WHERE is_confirmed = 0
            AND deleted_at IS NULL
            AND credit_card_id IS NOT NULL
            AND account_id IS NULL
            AND adjustment_type IS NULL
            AND (
                installment_id IS NOT NULL
                OR invoice_id IN (
                    SELECT id FROM invoices
                     WHERE total_amount > 0 AND paid_amount >= total_amount - 0.005
                )
            )`,
    );
    await rt.execute(
        `INSERT OR REPLACE INTO settings (key, value, updated_at) VALUES (?, '1', ?)`,
        [DONE_KEY, now],
    );
}

/**
 * Cura retroativa da fatura zumbi (ADR 0068): a lógica vive em `db/zombieInvoiceCleanup`
 * (executor injetado — importar o client de lá criaria ciclo no boot; e é assim que o
 * dbHarness a testa em SQLite real). Aqui só o gate de idempotência, no padrão dos demais
 * backfills. Restore de backup antigo re-roda naturalmente: as settings restauradas não
 * têm a chave.
 */
/**
 * Cura da duplicação do catálogo de fábrica (ADR 0119). SEM guarda de DONE_KEY, ao
 * contrário dos outros backfills: a detecção é um GROUP BY em algumas dezenas de
 * linhas e sai antes de escrever quando não há duplicata — barato o bastante pra
 * rodar sempre, e assim também pega uma duplicata que chegue depois (oplog de um
 * aparelho que já estava sujo).
 */
async function healDuplicateSeedCategories(rt: DbRuntime) {
    const result = await dedupeSeedCategories(
        (sql, params) => rt.execute(sql, params as never) as Promise<{ rows: any[] }>,
    );
    if (result.mergedCategories > 0 || result.mergedSubcategories > 0) {
        console.log(
            `Seed dedupe: ${result.mergedCategories} categorias e ${result.mergedSubcategories} subcategorias duplicadas absorvidas.`,
        );
    }
}

async function backfillZombieInvoiceCleanup(rt: DbRuntime) {
    const DONE_KEY = 'zombie_invoice_cleanup_v1';
    const guard = await rt.execute(
        `SELECT value FROM settings WHERE key = ?`,
        [DONE_KEY],
    );
    if (guard.rows.length > 0) return;

    await cleanupZombieInvoiceArtifacts((sql, params) => rt.execute(sql, params as never));

    await rt.execute(
        `INSERT OR REPLACE INTO settings (key, value, updated_at) VALUES (?, '1', ?)`,
        [DONE_KEY, new Date().toISOString()],
    );
}

export async function bootstrapDatabase(rt: DbRuntime, migrations: MigrationBundle): Promise<void> {
    try {
        console.log('Running database migrations...');
        await runMigrations(rt, migrations);
        await repairLegacySchema(rt);
        await backfillInvoiceWindows(rt);
        await backfillRecurringTags(rt);
        await backfillRecurringNotes(rt);
        await backfillAdvanceChargeReminders(rt);
        await backfillPendingCardEntries(rt);
        await backfillZombieInvoiceCleanup(rt);
        await healDuplicateSeedCategories(rt);
        console.log('Migrations completed successfully.');
    } catch (error) {
        console.error('Migration failed:', error);
        throw error;
    }

}
